import type { ComputedRef, Ref } from 'vue'

import type {
  FinalVideoPlaybackSource,
  StoryboardWorkspaceState,
  UploadedWorkspaceVideo,
} from './types'

interface UseFinalDeliverySourceControlOptions {
  systemFinalVideoUrl: ComputedRef<string>
  uploadedFinalVideo: ComputedRef<UploadedWorkspaceVideo | null>
  audioMixDeliveryVideo: ComputedRef<UploadedWorkspaceVideo | null>
  preferredFinalVideoSource: Ref<FinalVideoPlaybackSource>
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: { successMessage?: string; silent?: boolean }) => Promise<void>
  success: (message: string) => void
  error: (message: string) => void
}

export function useFinalDeliverySourceControl(options: UseFinalDeliverySourceControlOptions) {
  async function persistPreferredSource(source: FinalVideoPlaybackSource, successMessage: string) {
    options.preferredFinalVideoSource.value = source
    const nextState = options.getStoryboardWorkspaceStatePayload()
    await options.persistStoryboardWorkspaceState(nextState, { successMessage, silent: false })
  }

  async function handleUseSystemAsDelivery() {
    if (!options.systemFinalVideoUrl.value) {
      options.error('当前还没有系统成片，暂时不能切回系统底版')
      return
    }

    await persistPreferredSource('system', '已切回系统成片')
  }

  async function handleUseUploadedAsDelivery() {
    if (!options.uploadedFinalVideo.value?.url) {
      options.error('当前还没有人工终版，暂时不能切换到人工交付源')
      return
    }

    await persistPreferredSource('uploaded_final', '已切换到人工终版')
  }

  async function handleUseAudioMixDelivery() {
    if (!options.audioMixDeliveryVideo.value?.url) {
      options.error('当前还没有声音交付版，先确认并应用一版混音预览')
      return
    }

    await persistPreferredSource('audio_mix_delivery', '已切换到声音交付版')
  }

  return {
    handleUseAudioMixDelivery,
    handleUseSystemAsDelivery,
    handleUseUploadedAsDelivery,
  }
}
